import { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import queryString from 'query-string'
import { CandleDataResponse, Collaterals, ICandlesWithPairId, Pairs } from 'src/types'
import { http } from '../services'
import {
  CopytradingLeadersParams,
  ICandleDaysOpens,
  ICandlePayload,
  ICriptoAlert,
  PairDataPerDayValue,
  QueryPayload,
  TCriptoAlerts,
  TCryptoCoins,
  TTop10Traders,
} from './types'
import { candlesResponseMapper } from '../utils/candlesResponseMapper'

const DAY_TIME_FRAME = 1440
const CANDLES_PREFIX = 'candle-loader/api/v1'

const CRYPTO_ALERTS_URL = 'calerts/api/v1/'

const CHANGE_NOW_URL = 'https://api.changenow.io/v1/'

const MARGEX_URL = 'security/api/v1/'

const PAIRS_URL = 'client-trade/api/v1/settings/pairsList'

export const fetchCryptoAlerts = async () => {
  const { data } = await http.get<AxiosResponse<TCriptoAlerts>>(`${CRYPTO_ALERTS_URL}alerts`)
  return data
}

export const fetchPairs = async () => {
  const { data } = await http.get<AxiosResponse<Pairs>>(PAIRS_URL, {
    unauthorized: true,
  })
  return data
}

export const fetchPairsDataPerDay = async () => {
  const { data } = await http.get<AxiosResponse<PairDataPerDayValue>>('candle-loader/api/v1/candles/getAllLastDay', {
    unauthorized: true,
  })

  return data
}

export const fetchCandle = async (payload: QueryPayload<ICandlePayload>) => {
  const query = payload.queryKey[1] as ICandlePayload | undefined
  const stQuery = queryString.stringify(query || {})

  if (!query?.pair) {
    return
  }

  const {
    data: { data },
  } = await http.get<AxiosResponse<CandleDataResponse>>(`candle-loader/api/v1/candles/short?period=5&${stQuery}`, {
    authorizationOff: true,
  })
  return { data: candlesResponseMapper(data), globalId: query.pair } as ICandlesWithPairId
}

export const fetchCoins = async () => {
  const { data } = await http.get<AxiosResponse<TCryptoCoins>>(`${CRYPTO_ALERTS_URL}coins`)
  return data
}

export const createCryptoAlerts = async (alert: ICriptoAlert) => {
  const { data } = await http.post<AxiosResponse<ICriptoAlert>>(`${CRYPTO_ALERTS_URL}alerts`, alert)
  return data
}

export const updateCryptoAlerts = async (alert: ICriptoAlert) => {
  const { id, ...rest } = alert

  const { data } = await http.patch<AxiosResponse<ICriptoAlert>>(`${CRYPTO_ALERTS_URL}alerts/${id}`, rest)
  return data
}

export const deleteCryptoAlert = async (alertId: string | number) => {
  return await http.delete<AxiosResponse<unknown>>(`${CRYPTO_ALERTS_URL}alerts/${alertId}`)
}

export const fetchEstimatedExchangeAmount = async (amount: string | number, from: string, to: string) => {
  const { data } = await http.get<AxiosResponse<unknown>>(
    `${CHANGE_NOW_URL}exchange-amount/${amount}/${from}_${to}?api_key=${process.env.CHANGE_NOW_API_KEY}`,
    { authorizationOff: true }
  )
  return data
}

export const login = async (payload: any) => {
  const { data }: any = await http.post(`${MARGEX_URL}login`, payload, { unauthorized: true })
  return data
}

export const signUp = async (payload: any) => {
  const { data }: any = await http.post(`${MARGEX_URL}registration`, payload, { authorizationOff: true })
  return data
}

export const confirmCode = async (payload: any) => {
  const { data } = await http.post<AxiosResponse<unknown>>(`${MARGEX_URL}registration/emailConfirmation`, payload, {
    authorizationOff: true,
  })
  return data
}

export const resendCode = async (payload: any) => {
  const urlParams = new URLSearchParams('')
  urlParams.set('email', payload.email)
  const { data } = await http.post<AxiosResponse<unknown>>(
    `${MARGEX_URL}registration/resendCode?${urlParams.toString()}`,
    {}
  )
  return data
}

export const fetchDayCandlesOpens = async () => {
  const nowInMs = dayjs().valueOf()
  const startTime = dayjs().subtract(1, 'd').valueOf()
  const params = { startTime, endTime: nowInMs, period: DAY_TIME_FRAME }

  const { data } = await http.get<AxiosResponse<ICandleDaysOpens>>(
    `${CANDLES_PREFIX}/candles/getAllCurrentDay?${queryString.stringify(params)}`,
    { authorizationOff: true }
  )

  return data
}

export const fetchCollaterals = async () => {
  const { data } = await http.get<AxiosResponse<{ data: Collaterals }>>(
    `https://margex.com/client-payment/api/v1/collateral`,
    {
      authorizationOff: true,
    }
  )
  return data
}

export const fetchTop10Traders = async () => {
  const { data } = await http.post<AxiosResponse<{ total: number; pammAccountResponseList: TTop10Traders }>>(
    'pamm-invest/api/v1/pamms/available/?max=10&offset=0',
    { roe: 'WEEK' },
    {
      authorizationOff: true,
    }
  )
  return data
}

export const fetchTop10TradersByRoeDesc = async () => {
  const { data } = await http.post<AxiosResponse<{ total: number; pammAccountResponseList: TTop10Traders }>>(
    `pamm-invest/api/v1/pamms/available/?max=10&offset=0&sort=PROFITABILITY_DESC`,
    { roe: 'WEEK' },
    {
      authorizationOff: true,
    }
  )
  return data
}

export const fetchLeaders = async (params: CopytradingLeadersParams) => {
  const { filters = { roe: 'WEEK' }, max, offset, sort } = params
  const queryParams = queryString.stringify({ max, offset, sort })

  const { data } = await http.post<AxiosResponse<{ total: number; pammAccountResponseList: TTop10Traders }>>(
    `pamm-invest/api/v1/pamms/available/?${queryParams}`,
    filters,
    {
      authorizationOff: true,
    }
  )
  return data
}
